/**
 * Storage-decoupled image descriptor consumed by the image viewer surfaces.
 *
 * Callers resolve their own display URLs (CDN transforms, signed URLs, blob
 * object-URLs) before handing images to the viewer — keeping `@kit/ui` free of
 * any storage / Supabase / Cloudflare dependency.
 */
export interface ViewerImage {
  /** Stable identity for React keys + navigation. Falls back to the array index. */
  id?: string;
  /** Display URL, already CDN-resolved / signed by the caller. */
  src: string;
  /** Optional smaller URL for the thumbnail strip. Falls back to `src`. */
  thumbnailSrc?: string;
  /** Accessible description. Falls back to `fileName` then a generic label. */
  alt?: string;
  /** Original file name, shown in the lightbox header. */
  fileName?: string;
  /** File size in bytes, formatted for display in the lightbox header. */
  fileSize?: number;
  /** Optional download URL. Defaults to `src`. */
  downloadUrl?: string;
}

/**
 * User-facing strings. All optional — English defaults are applied internally,
 * so `@kit/ui` needs no i18n provider. Localized surfaces pass translated
 * values (e.g. from `t('common:imageViewer.*')`).
 */
export interface ImageViewerTranslations {
  close?: string;
  previous?: string;
  next?: string;
  zoomIn?: string;
  zoomOut?: string;
  resetZoom?: string;
  fullscreen?: string;
  exitFullscreen?: string;
  download?: string;
  /** Shown in the thumbnail trigger when there is no image. */
  noImage?: string;
}

export const DEFAULT_IMAGE_VIEWER_TRANSLATIONS: Required<ImageViewerTranslations> =
  {
    close: 'Close',
    previous: 'Previous',
    next: 'Next',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    resetZoom: 'Reset zoom',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    download: 'Download',
    noImage: 'No image',
  };

/** Inline aspect-ratio options for the gallery hero / single-image surfaces. */
export type ImageAspectRatio = 'auto' | '1/1' | '4/3' | '3/4' | '16/9';

export const MIN_ZOOM = 50;
export const MAX_ZOOM = 200;
export const ZOOM_STEP = 25;
export const DEFAULT_ZOOM = 100;
